/**
 * DragDivider Component
 * 
 * A draggable divider bar that allows users to set the boundary between
 * major and minor prizes by dragging. Provides visual feedback and 
 * accessibility features for both mouse and keyboard interaction.
 * 
 * @module TabPicker.DragDivider
 */

import React, { useState, useRef, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { cn } from '../../utils/cn';

interface DragDividerProps {
  position: number; // Current position (0 to totalTiers)
  totalTiers: number; // Total number of prize tiers
  onPositionChange: (newPosition: number) => void;
  className?: string;
}

export const DragDivider: React.FC<DragDividerProps> = ({
  position,
  totalTiers,
  onPositionChange,
  className
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const dividerRef = useRef<HTMLDivElement>(null);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: dndIsDragging
  } = useDraggable({
    id: 'prize-divider',
    data: { position }
  });

  // Sync dragging state
  useEffect(() => {
    setIsDragging(dndIsDragging);
  }, [dndIsDragging]);

  // Handle touch events to prevent page scrolling
  const handleTouchStart = (event: React.TouchEvent) => {
    // Prevent page scrolling when starting to drag
    event.preventDefault();
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    // Prevent page scrolling during drag
    if (isDragging) {
      event.preventDefault();
    }
  };

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    let newPosition = position;
    
    switch (event.key) {
      case 'ArrowUp':
        newPosition = Math.max(0, position - 1);
        break;
      case 'ArrowDown':
        newPosition = Math.min(totalTiers, position + 1);
        break;
      case 'PageUp':
        newPosition = Math.max(0, position - 3);
        break;
      case 'PageDown':
        newPosition = Math.min(totalTiers, position + 3);
        break;
      case 'Home':
        newPosition = 0;
        break;
      case 'End':
        newPosition = totalTiers;
        break;
      default:
        return;
    }
    
    event.preventDefault();
    onPositionChange(newPosition);
  };

  // Format aria description
  const getAriaValueText = () => {
    if (position === 0) return "All prizes are minor";
    if (position === totalTiers) return "All prizes are major";
    return `Major prizes: ${position} of ${totalTiers}`;
  };

  // Calculate transform style with smooth snapping
  const style = transform ? {
    transform: `translate3d(0, ${transform.y}px, 0)`,
    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
  } : {
    transition: 'transform 0.2s ease-out',
  };

  return (
    <div 
      className={cn(
        "relative flex items-center justify-center py-2 group",
        "transition-all duration-200",
        className
      )}
    >
      {/* Divider Line */}
      <div 
        className={cn(
          "w-full h-0.5 bg-gradient-to-r from-transparent via-gray-400 to-transparent",
          "transition-all duration-200",
          isDragging && "via-blue-500"
        )}
      />
      
      {/* Draggable Handle */}
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "absolute flex items-center justify-center",
          // Mobile-first sizing: larger hit target and more prominent
          "w-20 h-12 tablet:w-16 tablet:h-10 bg-white border-3 border-gray-400 rounded-lg shadow-md",
          "cursor-grab hover:border-blue-400 hover:shadow-lg",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          "transition-all duration-200 select-none",
          isDragging && "cursor-grabbing border-blue-500 shadow-xl scale-110 z-10",
          // Enhanced touch optimization
          "touch-manipulation touch-pan-y-disabled",
          // Prevent scrolling on mobile during drag
          "overscroll-contain"
        )}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        {...attributes}
        {...listeners}
        tabIndex={0}
        role="slider"
        aria-orientation="vertical"
        aria-valuenow={position}
        aria-valuemin={0}
        aria-valuemax={totalTiers}
        aria-valuetext={getAriaValueText()}
        aria-label="Drag to set major/minor prize boundary"
      >
        {/* Grip Icon */}
        <svg 
          className={cn(
            "w-5 h-5 tablet:w-4 tablet:h-4 text-gray-600 transition-colors",
            isDragging && "text-blue-600",
            "hover:text-gray-800"
          )}
          fill="currentColor" 
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <circle cx="4" cy="4" r="1.5" />
          <circle cx="12" cy="4" r="1.5" />
          <circle cx="4" cy="8" r="1.5" />
          <circle cx="12" cy="8" r="1.5" />
          <circle cx="4" cy="12" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
        </svg>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div className={cn(
          "absolute left-1/2 transform -translate-x-1/2 -translate-y-full",
          "mb-3 tablet:mb-2 px-4 py-3 tablet:px-3 tablet:py-2 bg-gray-900 text-white text-base tablet:text-sm rounded-md",
          "whitespace-nowrap pointer-events-none z-20",
          "opacity-0 animate-fadeIn"
        )}>
          <div className="text-center">
            <div className="font-medium">Drag to set boundary</div>
            <div className="text-sm tablet:text-xs text-gray-300 mt-1">
              {getAriaValueText()}
            </div>
          </div>
          {/* Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2">
            <div className="border-4 border-transparent border-t-gray-900" />
          </div>
        </div>
      )}

      {/* Live region for screen readers */}
      <div 
        role="status" 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
      >
        {isDragging && getAriaValueText()}
      </div>
    </div>
  );
};

export default DragDivider;